import { useEffect, useState } from 'react';
import './recruiting-view.styles.scss';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { CONTACTS_PER_DAY, CONTACT_TYPES, MAX_SCOUT_LEVEL, classScore, graduating, offerLimit, openScholarships, recruitingOpen } from '@shared/recruiting.js';
import { STATES } from '@shared/places.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { contactsLeft as contactsLeftOf, useScouting } from '@/utils/useScouting';
import { positions } from '@/utils/positions';
import { recruitNews } from '@/utils/recruitNews';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import ContactsLeft from '@/components/SHARED/contacts-left/contacts-left.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const SHOWN = 30;
const LEVEL_ORDER = { high: 0, medium: 1, low: 2 };
const RATINGS = [
    ['off_3pt', '3PT'], ['off_shooting', 'Mid-range'], ['off_drive', 'Driving'],
    ['off_passing', 'Passing'], ['off_rebounds', 'Off. reb'], ['off_smarts', 'Off. IQ'],
    ['def_speed', 'Perimeter D'], ['def_steals', 'Steals'], ['def_smarts', 'Def. IQ'],
    ['def_paint', 'Interior D'], ['def_blocks', 'Blocks'], ['def_rebounds', 'Def. reb'],
];
const SCOUT_LABELS = ['First look', 'Scouted once', 'Scouted twice', 'Fully scouted'];
const VIEWS = [['interested', 'Interested in us'], ['board', 'All recruits'], ['mine', 'My offers'], ['decided', 'Committed'], ['classes', 'Classes']];
const NEED_LABELS = { big: 'Big need', need: 'Need', set: 'Covered' };

// College recruiting (summary/recruits, plus the manager's own
// scouting/{teamId}). myTeam: the manager's team document; without it
// (admins) the page is read-only.
const RecruitingView = ({ league, teams, teamLink, myTeam }) => {

    const [ recruits, setRecruits ] = useState(undefined);
    const [ view, setView ] = useState(myTeam ? 'interested' : 'board');
    const [ pos, setPos ] = useState(0);
    const [ stars, setStars ] = useState(0);
    const [ inState, setInState ] = useState(false);
    const [ needsOnly, setNeedsOnly ] = useState(false);
    const [ shown, setShown ] = useState(SHOWN);
    const [ open, setOpen ] = useState(null);
    const [ busy, setBusy ] = useState(null);
    const [ alertMessage, setAlertMessage ] = useState(null);
    // A contact waiting for the manager's OK: {c, type}.
    const [ contacting, setContacting ] = useState(null);
    const myId = myTeam?.id;

    useEffect(() => {
        let unsub;
        listenToDoc({ 'path': `leagues/${league.id}/summary/recruits`, 'callback': setRecruits, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [league.id])

    const scouting = useScouting(league.id, myId);

    if (recruits === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    const year = recruits?.year;
    const recruiting = recruitingOpen(league) && year === (league.season.year ?? 1);
    const day = league.season.day ?? 0;
    const signingDay = league.season.totalDays;
    const cards = Object.values(recruits?.players ?? {});
    const myScout = scouting?.year === year ? scouting : null;
    const report = (id) => myScout?.recruits?.[id];
    const contactsLeft = contactsLeftOf(myScout, league);
    const scholarships = myTeam ? openScholarships(myTeam, league.settings.rosterSize) : 0;
    const myOffers = cards.filter(c => c.status === 'available' && c.offers.includes(myId));
    const limit = offerLimit(scholarships);
    const team = (id) => teams?.[id];
    const news = recruitNews(recruits, league, myId, teams);
    // How a card is marked for this manager: committed to you, chose
    // someone else (after you offered or contacted them), offered, contacted.
    const markOf = (c, mine, offered) => !myTeam ? null :
        c.committedTo === myId ? 'signed' :
        c.committedTo && (offered || mine) ? 'lost' :
        offered ? 'offered' : mine ? 'contacted' : null;
    const needs = myTeam ? teamNeeds(myTeam, league.settings.rosterSize) : [];
    const needed = new Set(needs.filter(n => n.need !== 'set').map(n => n.pos));
    const myLevel = (c) => LEVEL_ORDER[c.interest?.[myId]] ?? 3;

    const list = cards
        .filter(c => view === 'board' ? c.status === 'available' :
            view === 'interested' ? c.status === 'available' && c.interest?.[myId] :
            view === 'mine' ? c.offers.includes(myId) || c.committedTo === myId || report(c.id) :
            c.status !== 'available')
        .filter(c => !pos || c.pos1 === pos)
        .filter(c => !needsOnly || needed.has(c.pos1))
        .filter(c => !stars || c.stars === stars)
        .filter(c => !inState || (myTeam?.state && c.homeState === myTeam.state))
        .sort((a, b) => view === 'decided' ? (b.committedDay ?? 99) - (a.committedDay ?? 99) || a.rank - b.rank :
            view === 'interested' ? myLevel(a) - myLevel(b) || a.rank - b.rank : a.rank - b.rank);

    async function act(command, data, key) {
        setBusy(key);
        const res = await UseCloudFunction(command, { leagueId: league.id, ...data });
        setBusy(null);
        if (res.error) setAlertMessage(res.error);
    }

    const levelChips = (c) => Object.entries(c.interest ?? {})
        .sort((a, b) => LEVEL_ORDER[a[1]] - LEVEL_ORDER[b[1]] || (team(a[0])?.abbr ?? '').localeCompare(team(b[0])?.abbr ?? ''))
        .map(([teamId, level]) => (
            <Link key={teamId} to={teamLink(teamId)} className={`interest ${level} ${teamId === myId ? 'mine' : ''}`}>
                {team(teamId)?.abbr ?? teamId} <span>{level}</span>
            </Link>
        ));

    return (
        <div className='recruiting-view'>
            <div className='g-card recruiting-head'>
                <h3>Recruiting{year ? ` · Class of season ${year + 1}` : ''} <HelpLink section='recruiting' label='How recruiting works' /></h3>
                <p>
                    {!recruits ? 'No recruiting class yet. One is drawn up when the season starts.' :
                        recruiting ? (day < signingDay ? `Day ${day} · Signing day is day ${signingDay}, the last day of the regular season.` : 'Signing day.') :
                        'Recruiting is closed. The class joins its schools in the offseason, when players develop.'}
                </p>
                {myTeam && recruiting && <ContactsLeft league={league} left={contactsLeft} />}
                {myTeam && recruiting &&
                    <p className='meta'>
                        <b>{scholarships}</b> scholarship{scholarships === 1 ? '' : 's'} open · {myTeam.recruits?.length ?? 0} committed · {myOffers.length} of {limit} offers out
                    </p>
                }
                {myTeam && recruiting &&
                    <p className='start-here'>
                        <b>Start here:</b> check your team&apos;s needs below, then open <b>Interested in us</b>: recruits who already have your school on their list, the most interested first.
                        Scout the ones at positions you need, pitch them, and offer the ones you want.
                    </p>
                }
                {myTeam && recruiting &&
                    <p className='meta'>
                        Offer the recruits you want: they commit only to schools that offered them. Each contact is a pitch (the reply hints at what the recruit values) or a scouting visit (the rating ranges narrow). Recruits care about playing time, winning, development, prestige, staying close to home and your rivalry and payback wins. Interest updates after each day&apos;s games.
                    </p>
                }
            </div>

            {myTeam && recruiting &&
                <div className='g-card team-needs'>
                    <h3>Team needs for next season</h3>
                    <p className='meta'>Seniors leave after this season. Each position usually carries {Math.round(league.settings.rosterSize / 5)} players. Tap a position to see its recruits.</p>
                    <div className='table-scroll'>
                        <table className='g-table needs-table'>
                            <thead>
                                <tr><th>Pos</th><th>Leaving</th><th>Back</th><th>Best back</th><th>Signed</th><th>Need</th></tr>
                            </thead>
                            <tbody>
                                {needs.map(n => (
                                    <tr key={n.pos} className={pos === n.pos ? 'picked' : ''} onClick={() => { setPos(pos === n.pos ? 0 : n.pos); setNeedsOnly(false); if (view === 'classes') setView('interested'); setShown(SHOWN); }}>
                                        <td><button className='link-btn' aria-pressed={pos === n.pos}>{positions[n.pos - 1].short}</button></td>
                                        <td className='num'>{n.leaving}</td>
                                        <td className='num'>{n.returning}</td>
                                        <td className='num'>{n.best ?? '–'}</td>
                                        <td className='num'>{n.commits}</td>
                                        <td><span className={`need-tag ${n.need}`}>{NEED_LABELS[n.need]}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className='meta'>Back: players returning next season. Best back: the best of them by overall today (your starters average {Math.round(needs[0]?.starters ?? 0)}). Signed: recruits committed to you. A position is a need when it&apos;s short of players or its best returning player is well below your starters.</p>
                </div>
            }

            {news.length > 0 &&
                <div className='g-card recruit-news' role='status'>
                    <h3>Since the last game</h3>
                    <ul>{news.map(n => <li key={n.key} className={n.good ? 'good' : 'bad'}>{n.text}</li>)}</ul>
                </div>
            }

            {myTeam && (myTeam.recruits?.length ?? 0) > 0 &&
                <div className='g-card'>
                    <h3>Your class</h3>
                    <ul className='class-list'>
                        {myTeam.recruits.map(r => (
                            <li key={r.id}>
                                <span><b>{r.fName} {r.lName}</b> <span className='meta'>{positions[r.pos1 - 1]?.short} · {height(r.height)}{r.homeState ? ` · ${STATES[r.homeState] ?? r.homeState}` : ''}</span></span>
                                <Stars n={r.stars} />
                            </li>
                        ))}
                    </ul>
                </div>
            }

            <div className={`view-tabs ${myTeam ? '' : 'read-only'}`} role='tablist' aria-label='View'>
                {VIEWS.filter(([key]) => myTeam || !['mine', 'interested'].includes(key)).map(([key, label]) => (
                    <button key={key} role='tab' aria-selected={view === key} className={`view-tab ${view === key ? 'active' : ''}`} onClick={() => { setView(key); setShown(SHOWN); }}>{label}</button>
                ))}
            </div>

            {view === 'classes' ?
                <ClassRankings cards={cards} teams={teams} teamLink={teamLink} myId={myId} /> :
                <>
                    <div className='filter-bar'>
                        <label htmlFor='recruit-pos'>
                            <span>Position</span>
                            <select id='recruit-pos' value={needsOnly ? 'needs' : pos} onChange={(e) => {
                                const v = e.target.value;
                                setNeedsOnly(v === 'needs');
                                setPos(v === 'needs' ? 0 : Number(v));
                                setShown(SHOWN);
                            }}>
                                <option value={0}>All</option>
                                {needed.size > 0 && <option value='needs'>Our needs</option>}
                                {positions.map((p, i) => <option key={p.short} value={i + 1}>{p.short}</option>)}
                            </select>
                        </label>
                        <label htmlFor='recruit-stars'>
                            <span>Stars</span>
                            <select id='recruit-stars' value={stars} onChange={(e) => { setStars(Number(e.target.value)); setShown(SHOWN); }}>
                                <option value={0}>Any</option>
                                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
                            </select>
                        </label>
                        {myTeam?.state &&
                            <div className='filter-field'>
                                <span>Location</span>
                                <label htmlFor='recruit-in-state' className='check'>
                                    <input id='recruit-in-state' type='checkbox' checked={inState} onChange={() => { setInState(!inState); setShown(SHOWN); }} />
                                    <span>In state</span>
                                </label>
                            </div>
                        }
                    </div>

                    {list.length === 0 ?
                        <div className='g-card'><p className='meta'>{view === 'mine' ? 'No offers or scouting yet.' :
                            view === 'interested' ? 'No recruits here have your school on their list yet. Try All recruits: offers and pitches raise interest, which updates after each day’s games.' :
                            'No recruits here.'}</p></div> :
                        <div className='recruit-list'>
                            {list.slice(0, shown).map(c => {
                                const mine = report(c.id);
                                const range = mine?.overall ?? c.overall;
                                const offered = c.offers.includes(myId);
                                const canAct = recruiting && myTeam && c.status === 'available';
                                const expanded = open === c.id;
                                const dest = c.committedTo && team(c.committedTo);
                                const mark = markOf(c, mine, offered);
                                return (
                                    <div key={c.id} className={`g-card recruit-card ${mark ? `mark-${mark}` : ''}`}>
                                        {mark === 'signed' &&
                                            <div className='signed-banner'>Committed to you{c.committedDay ? ` · day ${c.committedDay}` : ''}</div>}
                                        <div className='recruit-top'>
                                            <div>
                                                <div className='recruit-name'>
                                                    {c.fName} {c.lName}
                                                    {mark === 'offered' && <span className='mark-tag offered'>Offered</span>}
                                                    {mark === 'contacted' && <span className='mark-tag contacted'>Contacted</span>}
                                                    {mark === 'lost' && <span className='mark-tag lost'>Chose {dest?.abbr ?? 'another school'}</span>}
                                                </div>
                                                <div className='meta'>
                                                    #{c.rank} · {positions[c.pos1 - 1]?.short}{c.pos2 ? `/${positions[c.pos2 - 1]?.short}` : ''} · {height(c.height)}{c.homeState ? ` · ${STATES[c.homeState] ?? c.homeState}` : ''}
                                                </div>
                                            </div>
                                            <div className='recruit-grade'>
                                                <Stars n={c.stars} />
                                                <span className='recruit-ovr'>{range[0]}–{range[1]} <span className='meta'>OVR</span></span>
                                            </div>
                                        </div>
                                        <div className='meta recruit-read'>
                                            {SCOUT_LABELS[mine?.level ?? 0]}
                                            {c.offers.length > 0 && <> · Offers: {c.offers.map((id, i) => <span key={id} className={id === myId ? 'my-offer' : ''}>{i ? ', ' : ''}{team(id)?.abbr ?? id}</span>)}</>}
                                        </div>
                                        {c.status === 'committed' && <div className='recruit-status'>Committed to <Link to={teamLink(c.committedTo)}>{dest?.name ?? c.committedTo}</Link>{c.committedDay ? ` (day ${c.committedDay})` : ''}</div>}
                                        {c.status === 'late' && <div className='recruit-status'>Signed late with <Link to={teamLink(c.committedTo)}>{dest?.name ?? c.committedTo}</Link></div>}
                                        {c.status === 'unsigned' && <div className='recruit-status'>Didn&apos;t sign with a school</div>}
                                        {c.status === 'available' && Object.keys(c.interest ?? {}).length > 0 &&
                                            <div className='interest-row' aria-label='Schools the player is interested in'><span className='meta'>Player&apos;s interest:</span>{levelChips(c)}</div>
                                        }
                                        {mine?.replies?.length > 0 &&
                                            <ul className='replies'>
                                                {mine.replies.map((r, i) => (
                                                    <li key={i}><span className='meta'>Day {r.day} · {CONTACT_TYPES.find(t => t.key === r.type)?.label}:</span> “{r.reply}”</li>
                                                ))}
                                            </ul>
                                        }
                                        {mine?.ratings &&
                                            <button className={`report-btn ${expanded ? 'open' : ''}`} aria-expanded={expanded} onClick={() => setOpen(expanded ? null : c.id)}>
                                                <span>
                                                    <b>{expanded ? 'Hide scouting report' : 'See scouting report'}</b>
                                                    <span className='report-meta'>{RATINGS.length} ratings · {SCOUT_LABELS[mine.level].toLowerCase()}</span>
                                                </span>
                                                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                                            </button>
                                        }
                                        {expanded && mine?.ratings &&
                                            <div className='rating-ranges'>
                                                {RATINGS.map(([key, label]) => (
                                                    <div key={key}><span className='meta'>{label}</span> <b>{mine.ratings[key][0]}–{mine.ratings[key][1]}</b></div>
                                                ))}
                                            </div>
                                        }
                                        {canAct &&
                                            <div className='contact-row'>
                                                <button className={`g-button small-btn ${offered ? '' : 'primary'} offer-btn ${busy ? 'disabled' : ''}`} disabled={Boolean(busy) || (!offered && (scholarships <= 0 || myOffers.length >= limit))} onClick={() => act('recruitOffer', { playerId: c.id, withdraw: offered }, `${c.id}-offer`)}>
                                                    {busy === `${c.id}-offer` ? <ThreeDotsLoader /> : offered ? 'Withdraw offer' : 'Offer scholarship'}
                                                </button>
                                                {CONTACT_TYPES.map(t => {
                                                    const off = contactsLeft <= 0 || (t.key === 'scout' ? (mine?.level ?? 0) >= MAX_SCOUT_LEVEL : scholarships <= 0);
                                                    return (
                                                        <button key={t.key} className={`g-button small-btn ${t.key === 'scout' ? 'scout-btn' : ''} ${busy || off ? 'disabled' : ''}`} disabled={Boolean(busy) || off} title={t.text} onClick={() => setContacting({ c, type: t.key })}>
                                                            {busy === `${c.id}-${t.key}` ? <ThreeDotsLoader /> : t.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        }
                                    </div>
                                )
                            })}
                            {list.length > shown &&
                                <button className='g-button' onClick={() => setShown(shown + SHOWN)}>Show more ({list.length - shown} left)</button>
                            }
                        </div>
                    }
                </>
            }
            <ModalConfirm
                show={Boolean(contacting)}
                cancel={() => setContacting(null)}
                message={contacting ? contactQuestion(contacting, contactsLeft) : ''}
                onApprove={() => {
                    const { c, type } = contacting;
                    setContacting(null);
                    act('recruitContact', { playerId: c.id, type }, `${c.id}-${type}`);
                }} />
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

// "Pitch Winning to Jordan Hall? This uses one of today's 3 contacts:
// you'll have 1 left."
function contactQuestion({ c, type }, left) {
    const name = `${c.fName} ${c.lName}`;
    const what = type === 'scout' ? `Scout ${name}?` :
        `Pitch ${CONTACT_TYPES.find(t => t.key === type)?.label ?? 'this'} to ${name}?`;
    const after = left - 1;
    return `${what} This uses one of today's ${CONTACTS_PER_DAY} contacts: ${after > 0 ? `you'll have ${after} left.` : "it's your last one today."}`;
}

// Where a team needs recruits, position by position: who leaves (seniors),
// who returns, the best returning player's overall, commits so far, and a
// rating. Visible information only.
function teamNeeds(team, rosterSize) {
    const players = Object.values(team.players ?? {});
    const target = Math.round(rosterSize / 5);
    const starters = players.map(p => p.overall).sort((a, b) => b - a).slice(0, 5);
    const starterAvg = starters.reduce((n, x) => n + x, 0) / Math.max(1, starters.length);
    return [1, 2, 3, 4, 5].map(pos => {
        const at = players.filter(p => p.pos1 === pos);
        const back = at.filter(p => !graduating(p));
        const commits = (team.recruits ?? []).filter(r => r.pos1 === pos).length;
        const best = back.length ? Math.max(...back.map(p => p.overall)) : null;
        const count = back.length + commits;
        const need = count < target - 1 || best === null ? 'big' :
            count < target || best < starterAvg - 6 ? 'need' : 'set';
        return { pos, leaving: at.length - back.length, returning: back.length, best, commits, need, starters: starterAvg };
    });
}

// The recruiting classes so far, best first.
const ClassRankings = ({ cards, teams, teamLink, myId }) => {
    const byTeam = {};
    for (const c of cards) {
        if (c.committedTo && c.status !== 'available') (byTeam[c.committedTo] ??= []).push(c);
    }
    const ranked = Object.entries(byTeam)
        .map(([id, list]) => ({ id, list, score: classScore(list) }))
        .sort((a, b) => b.score - a.score || (teams?.[a.id]?.name ?? '').localeCompare(teams?.[b.id]?.name ?? ''));
    if (!ranked.length) return <div className='g-card'><p className='meta'>No commitments yet.</p></div>;
    return (
        <div className='g-card'>
            <h3>Class rankings</h3>
            <ol className='class-rankings'>
                {ranked.slice(0, 25).map(({ id, list }) => {
                    const t = teams?.[id];
                    const count = (n) => list.filter(c => c.stars === n).length;
                    return (
                        <li key={id} className={id === myId ? 'mine' : ''}>
                            {t && <TeamBadge team={t} size='sm' />}
                            <Link to={teamLink(id)}>{t?.name ?? id}</Link>
                            <span className='meta class-mix'>
                                {list.length} · {[5, 4, 3].filter(n => count(n)).map(n => `${count(n)}×${n}★`).join(' ')}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

const Stars = ({ n }) => (
    <span className='stars' aria-label={`${n} star${n === 1 ? '' : 's'}`}>
        {'★'.repeat(n)}<span className='stars-off'>{'★'.repeat(5 - n)}</span>
    </span>
);

const height = (h) => `${Math.floor(h / 12)}'${h % 12}"`;

export default RecruitingView
